// This is the new table as part of the new design iteration
import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import { Link } from 'react-router-dom';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { Flex, Box, xcss, Inline, Text } from '@atlaskit/primitives';
import Button, { LoadingButton } from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import AddIcon from '@atlaskit/icon/glyph/add';
import ExportIcon from '@atlaskit/icon/glyph/export';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import {
  ColGroup,
  CopyableText,
  TableBody,
  TableHead,
  ExpandableRow,
  CustomSearch,
  FormattedAddress,
  useNotification,
  NotificationTypes,
  errorNotificationBuilder,
  useAddCustomerModal,
  useFeatureToggle,
} from '@css-xp/base';
import { EndCustomerContacts } from '@css-xp/contact-resources';
import {
  emptyStatePropForSoldToAddressTable,
  emptyStatePropForWrongSearch,
  errorStateProp,
  NoData,
} from '@css-xp/ppc-common';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import {
  routePaths,
  SUBSCRIPTION_CONSTANTS,
  defaultBillingProfileSortOptions,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { getCompanyNameRoute, getRouteUrl } from '@css-xp/ppc-utils';
import { AggregatedShipToParty, AggregatedShipToPartyConnection } from '@css-xp/ppc-graphtypes';
import {
  Maybe,
  InvoiceGroupsForInvoiceGroupIds,
  InvoiceGroupsForShipToPartyNames,
  InvoiceGroup,
} from '@css-xp/graphtypes/src/generated-types-new';
import { ListPageLoader } from '@css-xp/ppc-page-loaders';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import {
  CustomerListTableContainer,
  BillingProfilesCount,
  BillingProfilesAddress,
  RightAlignedTd,
  EndCustomerContactsContainer,
} from './customer-list-table.styles';
import { fetchInvoiceGroupsCountBySoldToAddressNames } from './queries/fetch-invoice-groups-counts-by-ship-to-party-names';
import { fetchInvoiceGroupsForSoldToAddressNames } from './queries/feth-invoice-groups-by-ship-to-party-names';
import { fetchEntitlementsCountByInvoiceGroupIds } from './queries/fetch-entitlements-counts-by-invoice-group-party-ids';
import { CustomerActions } from './components/customer-actions';

const BILLING_PROFILES_TO_SHOW_ON_EXPAND = 10;

export interface FormData {
  searchTerm: string;
}

export type CustomerListTableProps = {
  txaId: string;
  addressResponse?: Maybe<AggregatedShipToPartyConnection>;
  customerListLoading: boolean;
  currentPage?: number;
  rowsPerPage?: number;
  onRowChange?: (_arg0: number) => void;
  onPageChange?: (_arg0: number) => void;
  error?: string | ApolloError;
  downloadCustomersListCSV?: () => void;
  customersListLoading?: boolean;
  onSearch?: (_: string) => void;
  refetch?: () => void;
  isSoldToAddressArchivalEnabled?: boolean;
  isDuplicateCustomerNameEnabled?: boolean;
};

const getShipToPartyFromBillingProfileResponse = (billingProfilesCountData: any, companyName: string) => {
  return billingProfilesCountData?.invoiceGroupsCountForShipToPartyNames?.find(
    (count: InvoiceGroupsForShipToPartyNames) => count?.shipToPartyName === companyName
  );
};

export const CustomerListTable = ({
  txaId,
  addressResponse,
  customerListLoading,
  error,
  currentPage,
  rowsPerPage,
  onRowChange,
  onPageChange,
  downloadCustomersListCSV,
  customersListLoading,
  onSearch,
  refetch,
  isSoldToAddressArchivalEnabled,
  isDuplicateCustomerNameEnabled,
}: CustomerListTableProps) => {
  // using map for state variables because we have nested data for each company, IG ID which will be easier to get and set with map
  const [companyNameToSubRowsLoading, setCompanyNameToSubRowsLoading] = useState<Map<string, boolean>>(new Map());
  const [companyNameToSubRowsError, setCompanyNameToSubRowsError] = useState<Map<string, boolean>>(new Map());
  const [invoiceGroupIdToSubsciptionsLoading, setInvoiceGroupIdToSubsciptionsLoading] = useState<Map<string, boolean>>(
    new Map()
  );
  const [invoiceGroupIdToSubsciptionsCount, setInvoiceGroupIdToSubsciptionsCount] = useState<Map<string, number>>(
    new Map()
  );
  const [subRows, setSubRows] = useState(new Map());
  const [isSearched, setIsSearched] = useState(false);
  const { openAddCustomerModal } = useAddCustomerModal();
  const [isEnabled] = useFeatureToggle();
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);

  const totalCount = addressResponse?.totalCount || 0;
  const shipToParties = addressResponse?.nodes;

  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const { sendErrorNotification, notificationSent, setNotificationSent } = useNotification();

  const {
    loading: billingProfilesCountLoading,
    data: billingProfilesCountData,
    error: billingProfilesCountError,
  } = useQuery(fetchInvoiceGroupsCountBySoldToAddressNames, {
    variables: {
      filterOptions: {
        not: [
          {
            key: 'status',
            value: SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE,
          },
        ],
      },
      shipToPartyNames: shipToParties?.map((shipToParty: AggregatedShipToParty) => shipToParty?.key),
      transactionAccountId: txaId,
    },
    skip: customerListLoading && !shipToParties?.length,
    client: ansClient,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  if (billingProfilesCountError) {
    if (!notificationSent) {
      const errorMessage = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader('Could not fetch billing profiles count')
        .withMessage('Please wait for a moment and then refresh the page.')
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(errorMessage);
      setNotificationSent(true);
    }
  }

  const shouldExpandBeDisabled = (companyName: string) => {
    if (billingProfilesCountLoading) {
      return false;
    }

    const shipToPartyObj = getShipToPartyFromBillingProfileResponse(billingProfilesCountData, companyName);

    const totalCount = shipToPartyObj?.totalCount;

    if (totalCount === null || totalCount <= 0) {
      return true;
    }

    return false;
  };

  const renderBillingProfilesCountByCompanyName = (companyName: string) => {
    if (billingProfilesCountLoading || billingProfilesCountError) {
      return null;
    }

    const shipToPartyObj = getShipToPartyFromBillingProfileResponse(billingProfilesCountData, companyName);

    const totalCount = shipToPartyObj?.totalCount;

    if (totalCount === -1 || totalCount === null) {
      return null;
    }

    if (totalCount === 0) {
      return (
        <Flex alignItems="center">
          <WarningIcon label="Warning icon" primaryColor={token('color.icon.warning')} />
          <Text color="color.text.subtlest">At least one billing profile must be linked to this customer</Text>
        </Flex>
      );
    }

    return (
      <span>
        {totalCount} active billing {totalCount === 1 ? 'profile' : 'profiles'}
      </span>
    );
  };

  const renderEntitlementsCountsByInvoiceGroupId = (invoiceGroupId: string) => {
    const entitlementsCount = invoiceGroupIdToSubsciptionsCount.get(invoiceGroupId);

    if (entitlementsCount === -1) {
      return <NoData />;
    }

    return <span>{entitlementsCount}</span>;
  };

  const getHeaders = () => {
    const headers = [
      {
        id: 'companyName',
        name: 'Customer and billing profiles',
        customClassNames: 'col-billing-profiles',
      },
      {
        id: 'customerContacts',
        name: 'Customer contacts',
        customClassNames: 'col-customer-contacts',
      },
      {
        id: 'subscriptions',
        name: 'Subscriptions',
        customClassNames: 'col-subscriptions',
      },
      {
        id: 'actions',
        name: 'Actions',
      },
    ];

    return headers;
  };

  const getSubRowsLoadingForCompanyName = (companyName: string) => {
    return companyNameToSubRowsLoading.get(companyName);
  };

  const renderInvoiceGroupNameWitLink = (billingProfileId: string, name: string) => {
    return (
      <Link
        to={getRouteUrl(routePaths.billingProfileDetails, {
          txaId,
          billingProfileId,
        })}
      >
        {name}
      </Link>
    );
  };

  const getSubRowsForCompanyName = (companyName: string, index: number) => {
    const subRowsForCompanyName = subRows.get(companyName) || [];
    const hasError = companyNameToSubRowsError.get(companyName);
    const companyNamePath = getCompanyNameRoute({ txaId, companyName });
    const link = (
      <Link to={`${companyNamePath}#billing-profiles`} data-testid="view-all-billing-profiles-link">
        View all billing profiles
      </Link>
    );

    if (hasError) {
      return (
        <tr
          data-testid="ship-to-table-row-show-more-less"
          key="ship-to-table-row-show-more-less"
          className="grouped-child"
        >
          <td key="1">
            <Box paddingBlockStart="space.050">
              <Flex alignItems="center" gap="space.100">
                {link}
              </Flex>
            </Box>
          </td>
          <td key="2" />
          <td key="3" />
          <td key="4" />
        </tr>
      );
    }

    return subRowsForCompanyName.map((invoiceGroup: InvoiceGroup, idx: number) => {
      if (idx === BILLING_PROFILES_TO_SHOW_ON_EXPAND) {
        const shipToPartyObj = getShipToPartyFromBillingProfileResponse(billingProfilesCountData, companyName);

        return (
          <tr
            data-testid="ship-to-table-row-show-more-less"
            key="ship-to-table-row-show-more-less"
            className="grouped-child"
          >
            <td key="1">
              <Box paddingBlockStart="space.050">
                <Flex alignItems="center" gap="space.100">
                  <Lozenge>+{shipToPartyObj?.totalCount - BILLING_PROFILES_TO_SHOW_ON_EXPAND}</Lozenge>
                  {link}
                </Flex>
              </Box>
            </td>
            <td key="2" />
            <td key="3" />
            <td key="4" />
          </tr>
        );
      }

      return (
        <tr
          data-testid={`ship-to-table-row-${invoiceGroup.id}`}
          key={`ship-to-table-row-${index}-${invoiceGroup.id}`}
          className="grouped-child"
        >
          <BillingProfilesAddress key="1">
            <Flex direction="column">
              {isRedesignFastFollowEnabled ? (
                <Inline space={'space.100'} alignBlock="center">
                  <Tooltip content={invoiceGroup.name}>
                    {renderInvoiceGroupNameWitLink(`${invoiceGroup.id}`, `${invoiceGroup.name}`)}
                  </Tooltip>

                  <Text size="medium" color="color.text.subtlest">
                    <CopyableText text={invoiceGroup?.invoiceGroupNumber || invoiceGroup?.id} />
                  </Text>
                </Inline>
              ) : (
                renderInvoiceGroupNameWitLink(`${invoiceGroup.id}`, `${invoiceGroup.name}`)
              )}

              <p>
                <FormattedAddress
                  addressLine1={invoiceGroup.shipToParty?.addressLine1}
                  addressLine2={invoiceGroup.shipToParty?.addressLine2}
                  city={invoiceGroup.shipToParty?.city}
                  country={invoiceGroup.shipToParty?.country}
                  postcode={invoiceGroup.shipToParty?.postcode}
                  state={invoiceGroup.shipToParty?.state}
                  hasLineBreak={false}
                />
              </p>
            </Flex>
          </BillingProfilesAddress>
          <RightAlignedTd key="2">
            <EndCustomerContactsContainer>
              <EndCustomerContacts
                invoiceGroupId={invoiceGroup.id as string}
                transactionAccountId={txaId}
                version={2}
                loaderOptions={{
                  isRightAligned: true,
                  marginTop: '12px',
                }}
                noDataClassName="end-customer-contacts-no-data"
              />
            </EndCustomerContactsContainer>
          </RightAlignedTd>
          <RightAlignedTd key="3">
            {invoiceGroupIdToSubsciptionsLoading.get(companyName) ? (
              <SkeletonLoaderWrapper>
                <SkeletonLoader width="40%" height="26px" marginBottom="0px" isRightAligned={true} />
              </SkeletonLoaderWrapper>
            ) : (
              renderEntitlementsCountsByInvoiceGroupId(invoiceGroup.id as string)
            )}
          </RightAlignedTd>
          <td key="4" />
        </tr>
      );
    });
  };

  const handleOnExpandRow = async (companyName: string) => {
    setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, true)));
    const shipToPartyObj = getShipToPartyFromBillingProfileResponse(billingProfilesCountData, companyName);

    const { data: invoiceGroupsData, error: invoiceGroupsError } = await ansClient.query({
      query: fetchInvoiceGroupsForSoldToAddressNames,
      fetchPolicy: 'network-only',
      variables: {
        id: txaId,
        searchOptions: {
          first: BILLING_PROFILES_TO_SHOW_ON_EXPAND,
          offset: 0,
          sort: defaultBillingProfileSortOptions,
        },
        filterOptions: [
          {
            key: 'or',
            value: JSON.stringify(shipToPartyObj?.shipToPartyIds?.map((shipToId: string) => ({ shipToId }))),
          },
          {
            key: 'not',
            value: JSON.stringify([{ status: SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE }]),
          },
        ],
      },
    });

    if (invoiceGroupsError) {
      setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, false)));
      setCompanyNameToSubRowsError((map) => new Map(map.set(companyName, true)));
      return;
    }

    const billingProfiles = invoiceGroupsData?.transactionAccount?.billingProfiles?.nodes || [];

    if (!billingProfiles.length) {
      setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, false)));
    }

    const isBillingProfilesCountMoreThanThreshold =
      invoiceGroupsData?.transactionAccount?.billingProfiles?.totalCount > BILLING_PROFILES_TO_SHOW_ON_EXPAND;

    setSubRows(
      (map) =>
        new Map(
          map.set(
            companyName,
            isBillingProfilesCountMoreThanThreshold
              ? [
                  ...billingProfiles,
                  {}, // Add a dummy row to show the "View all billing profiles" link
                ]
              : billingProfiles
          )
        )
    );
    setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, false)));
    setCompanyNameToSubRowsError((map) => new Map(map.set(companyName, false)));
    setInvoiceGroupIdToSubsciptionsLoading((map) => new Map(map.set(companyName, true)));

    const { data: entitlementsData, error: entitlementsError } = await ansClient.query({
      query: fetchEntitlementsCountByInvoiceGroupIds,
      fetchPolicy: 'network-only',
      variables: {
        transactionAccountId: txaId,
        invoiceGroupIds: billingProfiles?.map((invoiceGroup: InvoiceGroup) => invoiceGroup.id),
      },
    });

    if (entitlementsError) {
      // set -1 for each invoice group id to show '-'
      const currentInvoiceGroupIdToSubsciptionsCount = invoiceGroupIdToSubsciptionsCount;
      billingProfiles?.forEach((invoiceGroup: InvoiceGroup) => {
        currentInvoiceGroupIdToSubsciptionsCount.set(invoiceGroup?.id ?? '', -1);
      });
      setInvoiceGroupIdToSubsciptionsCount(currentInvoiceGroupIdToSubsciptionsCount);
      setInvoiceGroupIdToSubsciptionsLoading((map) => new Map(map.set(companyName, false)));
    }

    const invoiceGroupIdsData = entitlementsData?.entitlementCountsForInvoiceGroupIds || [];
    const currentInvoiceGroupIdToSubsciptionsCount = invoiceGroupIdToSubsciptionsCount;
    invoiceGroupIdsData.forEach((invoiceGroup: InvoiceGroupsForInvoiceGroupIds) => {
      currentInvoiceGroupIdToSubsciptionsCount.set(
        invoiceGroup.invoiceGroupId ?? '',
        invoiceGroup.totalEntitlementsCount ?? -1
      );
    });

    setInvoiceGroupIdToSubsciptionsCount(currentInvoiceGroupIdToSubsciptionsCount);

    setInvoiceGroupIdToSubsciptionsLoading((map) => new Map(map.set(companyName, false)));
  };

  const renderTableBody = () => {
    if (!shipToParties) {
      return null;
    }

    return shipToParties.map((row: AggregatedShipToParty, index: number) => {
      const companyName = row.key;
      const companyNamePath = getCompanyNameRoute({ txaId, companyName });

      const rows = [
        [
          <td key="1">
            <BillingProfilesCount>
              <Tooltip content={companyName} tag="div">
                <Link to={companyNamePath}>{companyName}&nbsp;</Link>
              </Tooltip>
              {billingProfilesCountLoading ? (
                <SkeletonLoaderWrapper testId="billing-profiles-count-loading">
                  <SkeletonLoader width="80px" marginBottom="0px" />
                </SkeletonLoaderWrapper>
              ) : (
                renderBillingProfilesCountByCompanyName(companyName)
              )}
            </BillingProfilesCount>
          </td>,
          <td key="2" />,
          <td key="3" />,
          <RightAlignedTd key="4">
            <CustomerActions
              transactionAccountId={txaId}
              name={companyName}
              onComplete={refetch}
              isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
              isDuplicateCustomerNameEnabled={isDuplicateCustomerNameEnabled}
            />
          </RightAlignedTd>,
        ],
      ];

      return (
        <ExpandableRow
          key={companyName}
          rowsAsCells={rows}
          subRows={getSubRowsForCompanyName(companyName, index)}
          isSubRowsLoading={getSubRowsLoadingForCompanyName(companyName)}
          isDefaultExpanded={false}
          onExpand={async () => {
            await handleOnExpandRow(companyName);
          }}
          onCollapse={() => {
            setCompanyNameToSubRowsLoading((map) => new Map(map.set(companyName, false)));
            setCompanyNameToSubRowsError((map) => new Map(map.set(companyName, false)));
          }}
          customLoader={<ExpandLoader />}
          isExpandDisabled={shouldExpandBeDisabled(companyName)}
        />
      );
    });
  };

  const handleOnSearch = useCallback(
    async (formData: FormData) => {
      const searchInput = formData.searchTerm;
      setIsSearched(!!searchInput);
      if (onSearch) {
        onSearch(searchInput);
      }
    },
    [onSearch]
  );

  const addCustomerButton = (
    <Button
      appearance="default"
      testId="add-customer-button"
      onClick={() => openAddCustomerModal({ transactionAccountId: txaId, onSuccess: refetch })}
      iconBefore={<AddIcon label="add" size="small" />}
    >
      Add customer
    </Button>
  );

  const beforeTable = (
    <Box
      xcss={xcss({
        marginBottom: 'space.200',
      })}
    >
      <Flex justifyContent="space-between">
        <CustomSearch
          placeholder="Search customers"
          helperMessage="Enter customer name"
          isClearable
          onSearch={handleOnSearch}
          isLoading={customerListLoading}
        />
        <Flex gap="space.100">
          <LoadingButton
            appearance="default"
            onClick={() => {
              downloadCustomersListCSV && downloadCustomersListCSV();
            }}
            testId="export-customers-as-csv"
            isLoading={customersListLoading}
            isDisabled={!totalCount}
            iconBefore={<ExportIcon label="Export icon" size="small" />}
          >
            Export as CSV
          </LoadingButton>
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
            {addCustomerButton}
          </AdvocateAccessWall>
        </Flex>
      </Flex>
    </Box>
  );

  const emptyStateProps = useMemo(() => {
    return isSearched ? emptyStatePropForWrongSearch : emptyStatePropForSoldToAddressTable([() => addCustomerButton]);
  }, [isSearched]);

  return (
    <CustomerListTableContainer
      testId="customer-list-table"
      totalCount={totalCount}
      isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      noResults={totalCount === 0}
      onPageChange={onPageChange}
      onRowChange={onRowChange}
      isLoading={customerListLoading}
      beforeTable={beforeTable}
      error={error}
      errorStateProps={errorStateProp}
      emptyStateProps={emptyStateProps}
      isSearched={isSearched}
      customLoader={
        <ListPageLoader
          hideHeader
          hidePageHeader
          shouldRightAlignLastCol
          hideActions
          hideFilters
          hideSearchBar
          columnsCount={4}
          columnsWidth="15%"
        />
      }
    >
      <ColGroup columns={getHeaders()} />
      <TableHead columns={getHeaders()} />
      <TableBody>{renderTableBody()}</TableBody>
    </CustomerListTableContainer>
  );
};

const ExpandLoader = () => {
  return (
    <>
      {Array.from({ length: 2 }).map((_, index) => (
        <tr
          className="grouped-child expandable-loader-v2"
          key={index}
          data-testid={`customer-list-expand-loader-${index}`}
        >
          <td>
            <SkeletonLoaderWrapper>
              <Flex direction="column">
                <SkeletonLoader width="40%" height="14px" marginBottom="0.5rem" />
                <SkeletonLoader width="50%" height="12px" />
              </Flex>
            </SkeletonLoaderWrapper>
          </td>
          <td className="end-customer-contacts-loader-column">
            <SkeletonLoaderWrapper>
              <Flex justifyContent="end">
                <SkeletonLoader
                  circle={true}
                  width="25%"
                  isRightAligned
                  height="32px"
                  count={5}
                  marginRight="-1px"
                  marginLeft="0"
                />
              </Flex>
            </SkeletonLoaderWrapper>
          </td>
          <td>
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="40%" height="26px" isRightAligned />
            </SkeletonLoaderWrapper>
          </td>
        </tr>
      ))}
    </>
  );
};
